// Sidebar Navigation Component
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Drawer,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Avatar,
    IconButton,
    Tooltip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MonitorIcon from '@mui/icons-material/Monitor';
import PeopleIcon from '@mui/icons-material/People';
import ForumIcon from '@mui/icons-material/Forum';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaymentsIcon from '@mui/icons-material/Payments';

const DRAWER_WIDTH = 260;

const menuItems = [
    { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/moderation', label: 'Moderation Queue', icon: <ReportProblemIcon /> },
    { path: '/monitoring', label: 'Live Monitoring', icon: <MonitorIcon /> },
    { path: '/users', label: 'User Management', icon: <PeopleIcon /> },
    { path: '/community', label: 'Community Rooms', icon: <ForumIcon /> },
    { path: '/analytics', label: 'Analytics & Logs', icon: <AnalyticsIcon /> },
    { path: '/support', label: 'Support Inbox', icon: <SupportAgentIcon /> },
    { path: '/articles', label: 'Articles', icon: <ArticleIcon /> },
    { path: '/broadcasts', label: 'Broadcasts', icon: <CampaignIcon /> },
    { path: '/subscriptions', label: 'Subscriptions', icon: <PaymentsIcon /> },
    { path: '/safety', label: 'Safety Settings', icon: <SecurityIcon />, adminOnly: true },
];

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin, isModerator, logout } = useAuth();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const filteredMenuItems = menuItems.filter(item =>
        !item.adminOnly || isAdmin
    );

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    bgcolor: '#1A1A2E',
                    color: 'white'
                }
            }}
        >
            {/* Logo Header */}
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#FF6B9D' }} />
                <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        Ovuloom
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Admin Dashboard
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />

            {/* Navigation Menu */}
            <List sx={{ flex: 1, px: 1, py: 2 }}>
                {filteredMenuItems.map((item) => (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            selected={location.pathname === item.path}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(255, 107, 157, 0.2)',
                                    '&:hover': { bgcolor: 'rgba(255, 107, 157, 0.3)' }
                                },
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                            }}
                        >
                            <ListItemIcon sx={{ color: location.pathname === item.path ? '#FF6B9D' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: 14,
                                    fontWeight: location.pathname === item.path ? 600 : 400
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />

            {/* User Info & Logout */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                    src={user?.photoURL}
                    sx={{ bgcolor: '#FF6B9D', width: 36, height: 36 }}
                >
                    {user?.email?.[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                        {user?.displayName || user?.email?.split('@')[0]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {isAdmin ? 'Admin' : isModerator ? 'Moderator' : 'User'}
                    </Typography>
                </Box>
                <Tooltip title="Logout">
                    <IconButton onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <LogoutIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
